import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, MapPin, Clock, Users } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Container from "@/components/editorial/Container";
import Eyebrow from "@/components/editorial/Eyebrow";
import SponsorLogo from "@/components/editorial/SponsorLogo";
import { fetchEvent, submitRsvp } from "@/lib/events";

const SITE_URL = "https://mujeres-motor.com";
const TYPE_LABELS = { screening: "Screening", panel: "Panel", networking: "Networking", summit: "Summit", workshop: "Workshop" };

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}
function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function EventDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, notFound: false, data: null });
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", guests: 0, notes: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setState({ loading: true, notFound: false, data: null });
    fetchEvent(slug)
      .then((data) => setState({ loading: false, notFound: false, data }))
      .catch((e) => setState({ loading: false, notFound: e.response?.status === 404, data: null }));
  }, [slug]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y email son obligatorios.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await submitRsvp(slug, form);
      // Phase 6.2 · GA4 event_rsvp
      import("@/lib/analytics").then(({ trackEvent }) =>
        trackEvent("event_rsvp", { event_slug: slug })
      );
      toast.success(data.message);
      setDone(true);
      window.scrollTo({ top: document.getElementById("rsvp")?.offsetTop - 80 || 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar RSVP");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return <SiteLayout><div className="min-h-screen flex items-center justify-center"><p className="text-charcoal/50">Cargando…</p></div></SiteLayout>;
  if (state.notFound) return <SiteLayout><div className="min-h-screen flex flex-col items-center justify-center gap-4"><p className="font-serif text-3xl">Evento no encontrado</p><button onClick={() => nav("/events")} className="underline">Ver agenda</button></div></SiteLayout>;

  const { event: ev, sponsors, related_story: story, related_episode: episode } = state.data;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: ev.title,
    startDate: ev.start_at,
    endDate: ev.end_at || undefined,
    eventStatus: ev.status === "cancelled" ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: { "@type": "Place", name: ev.venue, address: ev.address || ev.city },
    description: ev.summary,
    image: ev.cover_image_url,
    organizer: { "@type": "Organization", name: "Mujeres Motor", url: SITE_URL },
  };

  return (
    <SiteLayout transparent>
      <></>

      {/* Hero */}
      <section data-testid="event-hero" className="relative min-h-[60vh] flex items-end bg-charcoal text-cream overflow-hidden pb-14">
        {ev.cover_image_url && <img src={ev.cover_image_url} alt={ev.title} className="absolute inset-0 w-full h-full object-cover opacity-55" />}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/40 via-charcoal/55 to-charcoal" />
        <Container className="relative z-10">
          <Link to="/events" className="inline-flex items-center gap-1.5 text-cream/60 text-xs uppercase tracking-[0.2em] hover:text-gold mb-6"><ArrowLeft className="h-3 w-3" />Eventos</Link>
          <p className="text-[10px] uppercase tracking-[0.32em] text-gold mb-4">{TYPE_LABELS[ev.event_type] || ev.event_type} · {ev.city}</p>
          <h1 className="font-serif font-light text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.03] max-w-4xl">{ev.title}</h1>
          <p className="mt-6 text-cream/75 text-lg max-w-2xl">{ev.summary}</p>
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3 text-sm text-cream/80">
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" />{fmtDate(ev.start_at)} · {fmtTime(ev.start_at)}</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" />{ev.venue}</div>
            {ev.capacity && <div className="flex items-center gap-2"><Users className="h-4 w-4 text-gold" />Aforo {ev.capacity}</div>}
          </div>
        </Container>
      </section>

      {/* Description */}
      {ev.description && (
        <section className="bg-cream py-16 md:py-20">
          <Container>
            <div className="max-w-2xl mx-auto prose prose-slate">
              <ReactMarkdown>{ev.description}</ReactMarkdown>
            </div>
          </Container>
        </section>
      )}

      {/* Speakers */}
      {ev.speakers && ev.speakers.length > 0 && (
        <section className="bg-cream-dark py-16 border-t border-charcoal/5" data-testid="event-speakers">
          <Container>
            <Eyebrow tone="terracotta" className="mb-7 block">Speakers</Eyebrow>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {ev.speakers.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="aspect-square mb-4 rounded-full overflow-hidden bg-charcoal-soft max-w-[180px] mx-auto">
                    {s.photo_url ? <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full bg-charcoal/10" />}
                  </div>
                  <h3 className="font-serif text-lg text-charcoal font-light">{s.name}</h3>
                  {s.role && <p className="text-xs text-charcoal/60 mt-1">{s.role}</p>}
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* Venue map */}
      {ev.venue_map_url && (
        <section className="bg-cream py-14">
          <Container>
            <div className="max-w-3xl mx-auto">
              <Eyebrow tone="terracotta" className="mb-5 block">Venue · {ev.venue}</Eyebrow>
              {ev.address && <p className="text-charcoal/70 mb-5 text-sm">{ev.address}</p>}
              <div className="aspect-[16/9] rounded-sm overflow-hidden border border-charcoal/10">
                <iframe src={ev.venue_map_url} width="100%" height="100%" loading="lazy" title="Mapa" />
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* Sponsors */}
      {sponsors && sponsors.length > 0 && (
        <section className="bg-cream-dark py-14 border-t border-charcoal/5">
          <Container>
            <p className="text-center text-[10px] uppercase tracking-[0.28em] text-terracotta mb-6">Presentado por</p>
            <div className="flex flex-wrap justify-center gap-8 md:gap-10 items-center">
              {sponsors.map((sp) => <SponsorLogo key={sp.id} sponsor={sp} tone="light" size={sp.tier === "founding" ? "lg" : sp.tier === "content" ? "sm" : "md"} />)}
            </div>
          </Container>
        </section>
      )}

      {/* RSVP */}
      <section id="rsvp" className="bg-charcoal text-cream py-20 md:py-28" data-testid="event-rsvp">
        <Container>
          <div className="max-w-2xl mx-auto">
            {done ? (
              <div data-testid="rsvp-success" className="bg-charcoal-soft border border-gold/30 p-10 rounded-sm text-center">
                <Eyebrow tone="gold" className="mb-4 block">RSVP confirmado</Eyebrow>
                <h2 className="font-serif text-3xl md:text-4xl font-light mb-4">Te reservamos lugar.</h2>
                <p className="text-cream/75">Recibirás los detalles logísticos por email 48 horas antes del evento.</p>
              </div>
            ) : ev.status !== "upcoming" ? (
              <div className="text-center">
                <p className="font-serif italic text-2xl text-cream/70">Este evento no está recibiendo RSVPs.</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-10">
                  <Eyebrow tone="gold" className="mb-5 block">RSVP</Eyebrow>
                  <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.08]">Reserva tu lugar.</h2>
                </div>
                <form onSubmit={submit} className="space-y-4">
                  <input type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={(e) => upd("website", e.target.value)} style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }} aria-hidden="true" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="rsvp-name" placeholder="Nombre completo *" value={form.name} onChange={(e) => upd("name", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" required />
                    <input data-testid="rsvp-email" type="email" placeholder="Email *" value={form.email} onChange={(e) => upd("email", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" required />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <input data-testid="rsvp-phone" placeholder="Teléfono" value={form.phone} onChange={(e) => upd("phone", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" />
                    <input data-testid="rsvp-city" placeholder="Ciudad" value={form.city} onChange={(e) => upd("city", e.target.value)} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none" />
                    <select data-testid="rsvp-guests" value={form.guests} onChange={(e) => upd("guests", Number(e.target.value))} className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream focus:border-gold focus:outline-none">
                      {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n} className="text-charcoal">{n === 0 ? "Sin acompañantes" : `${n} acompañante${n > 1 ? "s" : ""}`}</option>)}
                    </select>
                  </div>
                  <textarea data-testid="rsvp-notes" placeholder="Notas (opcional)" value={form.notes} onChange={(e) => upd("notes", e.target.value)} rows="3" className="w-full bg-transparent border border-cream/20 rounded-sm px-4 py-3 text-sm text-cream placeholder:text-cream/40 focus:border-gold focus:outline-none resize-none" />
                  <button type="submit" data-testid="rsvp-submit" disabled={submitting} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-8 py-3.5 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms] disabled:opacity-60">
                    {submitting ? "Enviando…" : "Confirmar RSVP"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              </>
            )}
          </div>
        </Container>
      </section>

      {/* Related */}
      {(story || episode) && (
        <section className="bg-cream py-16 border-t border-charcoal/5">
          <Container>
            <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-8">
              {story && (
                <Link to={`/stories/${story.slug}`} className="p-6 border border-charcoal/10 rounded-sm hover:border-terracotta transition-colors group">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2">Historia relacionada</p>
                  <h3 className="font-serif text-xl text-charcoal group-hover:text-terracotta">{story.name}</h3>
                  <p className="text-sm text-charcoal/60 mt-1">{story.city}</p>
                </Link>
              )}
              {episode && (
                <Link to={`/podcast/${episode.slug}`} className="p-6 border border-charcoal/10 rounded-sm hover:border-terracotta transition-colors group">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-terracotta mb-2">Episodio relacionado</p>
                  <h3 className="font-serif text-xl text-charcoal group-hover:text-terracotta">{episode.title}</h3>
                  <p className="text-sm text-charcoal/60 mt-1">EP {String(episode.number).padStart(2, "0")} · {episode.guest_name}</p>
                </Link>
              )}
            </div>
          </Container>
        </section>
      )}
    </SiteLayout>
  );
}
